import type { LucideIcon } from 'lucide-react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';

/**
 * The three states every data surface has, made impossible to forget.
 *
 * A list that renders nothing while loading, nothing when empty and nothing on
 * error looks identical in all three - and the user reads all three as "I have
 * no invoices". Having named components for each is what stops the empty case
 * being the default rendering of a failure.
 */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden />;
}

/** A skeleton shaped like the content, not a generic spinner. */
export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2 p-3" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <div
              key={c}
              className="skeleton h-8 rounded"
              // Varying widths so it reads as content rather than a loading bar.
              style={{ flex: c === 0 ? 3 : c === cols - 1 ? 1 : 2 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      {Icon ? (
        <div className="flex size-11 items-center justify-center rounded-full bg-muted">
          <Icon className="size-5 text-muted-foreground" aria-hidden />
        </div>
      ) : null}
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? <p className="max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * A failure, said out loud, with a way to retry.
 *
 * Shows the server's own message rather than "Something went wrong" - the API
 * already writes messages a user can act on ("A billable expense needs a
 * customer"), and replacing them with a generic banner throws that away.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  // A 403 or a 404 is not a failure that a second attempt fixes: the record
  // is not there, or it is not yours. Offering "Try again" under "That
  // invoice does not exist" invites a click that does nothing, and a click
  // that does nothing reads as a bug.
  const retryable = !(error instanceof ApiError && (error.statusCode === 403 || error.statusCode === 404));
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="size-5 text-destructive" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">That did not load</p>
        <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
      </div>
      {onRetry && retryable ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
